import type { WizardDraftPatch, WizardDraftResponse } from '@/types/api'
import { wizardIssueKeys } from './validators.ts'

export interface WizardFormValues {
  account_type: string
  exchange_rate: number
  leverage: string
  mt_password: string
  mt_password_confirm: string
  leverage_policy_accepted: boolean
  full_name: string
  birth_place: string
  birth_date: string
  nationality_code: string
  identity_type: string
  identity_number: string
  npwp_number: string
  gender: string
  marital_status: string
  spouse_name: string
  mother_maiden_name: string
  address_street: string
  address_kelurahan: string
  address_kecamatan: string
  address_kota: string
  address_provinsi: string
  home_postal_code: string
  home_phone: string
  home_fax: string
  mobile_phone: string
  email: string
  home_ownership_status: string
  home_ownership_notes: string
  account_purpose: string
  account_purpose_notes: string
  prior_transactions: boolean
  investment_experience: boolean
  family_in_regulator: boolean
  declared_bankrupt: boolean
  occupation: string
  occupation_other: string
  company_name: string
  industry_field: string
  position: string
  years_at_company: number
  years_at_previous_company: number
  office_address: string
  office_postal_code: string
  office_phone: string
  office_fax: string
  annual_income: string
  njop_value: number
  home_location: string
  total_savings: number
  total_assets: number
  other_assets: string
  bank_name: string
  bank_branch: string
  account_number: string
  account_holder: string
  bank_phone: string
  bank_account_type: string
  bank_address: string
  bank_city: string
  bank_province: string
  bank_swift: string
  bank_iban: string
  emergency_name: string
  emergency_address: string
  emergency_postal_code: string
  emergency_phone: string
  emergency_relation: string
  doc_selfie: string
  doc_selfie_id: string
  doc_identity: string
  doc_identity_id: string
}

export const defaultWizardValues: WizardFormValues = {
  account_type: 'ZERO',
  exchange_rate: 10000,
  leverage: '1:100',
  mt_password: '',
  mt_password_confirm: '',
  leverage_policy_accepted: false,
  full_name: '',
  birth_place: '',
  birth_date: '',
  nationality_code: 'ID',
  identity_type: 'KTP',
  identity_number: '',
  npwp_number: '',
  gender: 'LAKI_LAKI',
  marital_status: 'TIDAK_KAWIN',
  spouse_name: '',
  mother_maiden_name: '',
  address_street: '',
  address_kelurahan: '',
  address_kecamatan: '',
  address_kota: '',
  address_provinsi: '',
  home_postal_code: '',
  home_phone: '',
  home_fax: '',
  mobile_phone: '',
  email: '',
  home_ownership_status: 'LAINNYA',
  home_ownership_notes: '',
  account_purpose: 'LAINNYA',
  account_purpose_notes: '',
  prior_transactions: false,
  investment_experience: false,
  family_in_regulator: false,
  declared_bankrupt: false,
  occupation: 'LAINNYA',
  occupation_other: '',
  company_name: '',
  industry_field: '',
  position: '',
  years_at_company: 0,
  years_at_previous_company: 0,
  office_address: '',
  office_postal_code: '',
  office_phone: '',
  office_fax: '',
  // TODO(product): monthly income is not collected; annual_income is the only income field.
  annual_income: '100_250_JUTA',
  njop_value: 0,
  home_location: '',
  total_savings: 0,
  total_assets: 0,
  other_assets: '',
  bank_name: '',
  bank_branch: '',
  account_number: '',
  account_holder: '',
  bank_phone: '',
  bank_account_type: 'TABUNGAN',
  bank_address: '',
  bank_city: '',
  bank_province: '',
  bank_swift: '',
  bank_iban: '',
  emergency_name: '',
  emergency_address: '',
  emergency_postal_code: '',
  emergency_phone: '',
  emergency_relation: '',
  doc_selfie: '',
  doc_selfie_id: '',
  doc_identity: '',
  doc_identity_id: '',
}

/** Name fields: silent-data-loss bug fix (round 1) — autosave must never blank a
 * value the user actually typed just because it fails `validPersonName` (e.g. an
 * underscore). Validation display + submit-gating already run independently in
 * AgreementFormPage (fieldIssues/issueText, the submit-time wizardIssueKeys check) —
 * this draft patch is not the place to enforce that, and never was for other
 * fields either (non-name `issues[key]`-gated fields below are unchanged, out of
 * scope for this fix). */
const NAME_KEYS = new Set([
  'full_name',
  'birth_place',
  'mother_maiden_name',
  'spouse_name',
  'emergency_name',
  'account_holder',
])

export function buildWizardDraftPatch(data: WizardFormValues): WizardDraftPatch {
  return trimStrings(buildRawPatch(data))
}

function trimStrings<T>(value: T): T {
  if (typeof value === 'string') return value.trim() as T
  if (Array.isArray(value)) return value.map(trimStrings) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, trimStrings(v)])) as T
  }
  return value
}

function buildRawPatch(data: WizardFormValues): WizardDraftPatch {
  const issues = wizardIssueKeys(data)
  const text = (key: string, value: string) => (!NAME_KEYS.has(key) && issues[key] ? '' : value)
  const money = (key: string, value: number) => (issues[key] ? undefined : value)
  return {
    account_preferences: {
      account_type: data.account_type,
      exchange_rate: data.exchange_rate,
      leverage: data.leverage,
      leverage_policy_accepted: data.leverage_policy_accepted,
    },
    personal_data: {
      full_name: text('full_name', data.full_name),
      birth_place: text('birth_place', data.birth_place),
      birth_date: text('birth_date', data.birth_date),
      nationality_code: data.nationality_code,
      identity_type: data.identity_type,
      identity_number: text('identity_number', data.identity_number),
      npwp_number: text('npwp_number', data.npwp_number) || null,
      gender: data.gender,
      spouse_name: text('spouse_name', data.spouse_name) || null,
      mother_maiden_name: text('mother_maiden_name', data.mother_maiden_name),
      marital_status: data.marital_status,
      address_street: text('address_street', data.address_street),
      address_kelurahan: data.address_kelurahan,
      address_kecamatan: data.address_kecamatan,
      address_kota: data.address_kota,
      address_provinsi: data.address_provinsi,
      home_postal_code: text('home_postal_code', data.home_postal_code),
      home_phone: text('home_phone', data.home_phone),
      home_fax: text('home_fax', data.home_fax),
      mobile_phone: data.mobile_phone,
      email: data.email,
      home_ownership_status: data.home_ownership_status,
      home_ownership_notes: text('home_ownership_notes', data.home_ownership_notes),
      account_purpose: data.account_purpose,
      account_purpose_notes: text('account_purpose_notes', data.account_purpose_notes),
      prior_transactions: data.prior_transactions,
      investment_experience: data.investment_experience,
      family_in_regulator: data.family_in_regulator,
      declared_bankrupt: data.declared_bankrupt,
    },
    employment: {
      occupation: data.occupation,
      occupation_other: text('occupation_other', data.occupation_other),
      company_name: text('company_name', data.company_name),
      industry_field: text('industry_field', data.industry_field),
      position: text('position', data.position),
      years_at_company: data.years_at_company,
      years_at_previous_company: data.years_at_previous_company,
      office_address: text('office_address', data.office_address),
      office_postal_code: text('office_postal_code', data.office_postal_code),
      office_phone: text('office_phone', data.office_phone),
      office_fax: text('office_fax', data.office_fax),
      annual_income: data.annual_income,
    },
    wealth: {
      njop_value: money('njop_value', data.njop_value),
      home_location: text('home_location', data.home_location),
      total_savings: money('total_savings', data.total_savings),
      total_assets: money('total_assets', data.total_assets),
      other_assets: text('other_assets', data.other_assets),
    },
    bank_accounts: [
      {
        bank_name: data.bank_name,
        branch: text('bank_branch', data.bank_branch),
        account_number: text('account_number', data.account_number),
        account_holder: text('account_holder', data.account_holder),
        account_phone: text('bank_phone', data.bank_phone),
        account_type: data.bank_account_type,
        bank_address: text('bank_address', data.bank_address),
        bank_city: text('bank_city', data.bank_city),
        bank_province: text('bank_province', data.bank_province),
        swift_code: text('bank_swift', data.bank_swift),
        iban: text('bank_iban', data.bank_iban),
        is_primary: true,
      },
    ],
    emergency_contact: {
      full_name: text('emergency_name', data.emergency_name),
      home_address: text('emergency_address', data.emergency_address),
      postal_code: text('emergency_postal_code', data.emergency_postal_code),
      phone_number: text('emergency_phone', data.emergency_phone),
      relationship: text('emergency_relation', data.emergency_relation),
    },
    documents: {
      selfie: data.doc_selfie,
      identity: data.doc_identity,
      selfie_document_id: data.doc_selfie_id,
      identity_document_id: data.doc_identity_id,
    },
  }
}

function str(v: unknown): string | undefined {
  return v != null && v !== '' ? String(v) : undefined
}

function num(v: unknown): number | undefined {
  return v != null && v !== '' ? Number(v) : undefined
}

function bool(v: unknown): boolean | undefined {
  return v != null ? Boolean(v) : undefined
}

export function mapDraftToForm(
  draft: WizardDraftResponse['draft_data'],
): Partial<WizardFormValues> {
  const personal = (draft.personal_data ?? {}) as Record<string, unknown>
  const employment = (draft.employment ?? {}) as Record<string, unknown>
  const wealth = (draft.wealth ?? {}) as Record<string, unknown>
  const documents = (draft.documents ?? {}) as Record<string, unknown>
  const bankAccounts = draft.bank_accounts
  const bank = (Array.isArray(bankAccounts) ? bankAccounts[0] : undefined) as
    | Record<string, unknown>
    | undefined
  const bankData = bank ?? {}
  const emergency = (draft.emergency_contact ?? {}) as Record<string, unknown>
  const prefs = (draft.account_preferences ?? {}) as Record<string, unknown>

  const mapped: Partial<WizardFormValues> = {}

  if (prefs.account_type) mapped.account_type = String(prefs.account_type)
  if (prefs.exchange_rate != null) mapped.exchange_rate = Number(prefs.exchange_rate)
  if (prefs.leverage) mapped.leverage = String(prefs.leverage)
  if (prefs.leverage_policy_accepted != null) {
    mapped.leverage_policy_accepted = Boolean(prefs.leverage_policy_accepted)
  }

  const personalFields: Array<[keyof WizardFormValues, string]> = [
    ['full_name', 'full_name'],
    ['birth_place', 'birth_place'],
    ['birth_date', 'birth_date'],
    ['nationality_code', 'nationality_code'],
    ['identity_type', 'identity_type'],
    ['identity_number', 'identity_number'],
    ['npwp_number', 'npwp_number'],
    ['gender', 'gender'],
    ['marital_status', 'marital_status'],
    ['spouse_name', 'spouse_name'],
    ['mother_maiden_name', 'mother_maiden_name'],
    ['address_street', 'address_street'],
    ['address_kelurahan', 'address_kelurahan'],
    ['address_kecamatan', 'address_kecamatan'],
    ['address_kota', 'address_kota'],
    ['address_provinsi', 'address_provinsi'],
    ['home_postal_code', 'home_postal_code'],
    ['home_phone', 'home_phone'],
    ['home_fax', 'home_fax'],
    ['mobile_phone', 'mobile_phone'],
    ['email', 'email'],
    ['home_ownership_status', 'home_ownership_status'],
    ['home_ownership_notes', 'home_ownership_notes'],
    ['account_purpose', 'account_purpose'],
    ['account_purpose_notes', 'account_purpose_notes'],
  ]
  for (const [formKey, draftKey] of personalFields) {
    const v = str(personal[draftKey])
    if (v) mapped[formKey] = v as never
  }
  if (bool(personal.prior_transactions) != null) mapped.prior_transactions = Boolean(personal.prior_transactions)
  if (bool(personal.investment_experience) != null) mapped.investment_experience = Boolean(personal.investment_experience)
  if (bool(personal.family_in_regulator) != null) mapped.family_in_regulator = Boolean(personal.family_in_regulator)
  if (bool(personal.declared_bankrupt) != null) mapped.declared_bankrupt = Boolean(personal.declared_bankrupt)

  const employmentFields: Array<[keyof WizardFormValues, string]> = [
    ['occupation', 'occupation'],
    ['occupation_other', 'occupation_other'],
    ['company_name', 'company_name'],
    ['industry_field', 'industry_field'],
    ['position', 'position'],
    ['office_address', 'office_address'],
    ['office_postal_code', 'office_postal_code'],
    ['office_phone', 'office_phone'],
    ['office_fax', 'office_fax'],
    ['annual_income', 'annual_income'],
  ]
  for (const [formKey, draftKey] of employmentFields) {
    const v = str(employment[draftKey])
    if (v) mapped[formKey] = v as never
  }
  if (num(employment.years_at_company) != null) mapped.years_at_company = Number(employment.years_at_company)
  if (num(employment.years_at_previous_company) != null) {
    mapped.years_at_previous_company = Number(employment.years_at_previous_company)
  }

  if (num(wealth.njop_value) != null) mapped.njop_value = Number(wealth.njop_value)
  if (str(wealth.home_location)) mapped.home_location = String(wealth.home_location)
  if (num(wealth.total_savings) != null) mapped.total_savings = Number(wealth.total_savings)
  if (num(wealth.total_assets) != null) mapped.total_assets = Number(wealth.total_assets)
  if (str(wealth.other_assets)) mapped.other_assets = String(wealth.other_assets)

  if (str(bankData.bank_name)) mapped.bank_name = String(bankData.bank_name)
  if (str(bankData.branch)) mapped.bank_branch = String(bankData.branch)
  if (str(bankData.account_number)) mapped.account_number = String(bankData.account_number)
  if (str(bankData.account_holder)) mapped.account_holder = String(bankData.account_holder)
  if (str(bankData.account_phone)) mapped.bank_phone = String(bankData.account_phone)
  if (str(bankData.account_type)) mapped.bank_account_type = String(bankData.account_type)
  if (str(bankData.bank_address)) mapped.bank_address = String(bankData.bank_address)
  if (str(bankData.bank_city)) mapped.bank_city = String(bankData.bank_city)
  if (str(bankData.bank_province)) mapped.bank_province = String(bankData.bank_province)
  if (str(bankData.swift_code)) mapped.bank_swift = String(bankData.swift_code)
  if (str(bankData.iban)) mapped.bank_iban = String(bankData.iban)

  if (str(emergency.full_name)) mapped.emergency_name = String(emergency.full_name)
  if (str(emergency.home_address)) mapped.emergency_address = String(emergency.home_address)
  if (str(emergency.postal_code)) mapped.emergency_postal_code = String(emergency.postal_code)
  if (str(emergency.phone_number)) mapped.emergency_phone = String(emergency.phone_number)
  if (str(emergency.relationship)) mapped.emergency_relation = String(emergency.relationship)

  if (str(documents.selfie)) mapped.doc_selfie = String(documents.selfie)
  if (str(documents.identity)) mapped.doc_identity = String(documents.identity)
  if (str(documents.selfie_document_id)) mapped.doc_selfie_id = String(documents.selfie_document_id)
  if (str(documents.identity_document_id)) mapped.doc_identity_id = String(documents.identity_document_id)

  return mapped
}
